import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Copy, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleCopy = () => {
    const { error, errorInfo } = this.state;
    const details = [
      `Error: ${error?.message}`,
      `Stack: ${error?.stack}`,
      `Component: ${errorInfo?.componentStack}`,
      `Timestamp: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `UserAgent: ${navigator.userAgent}`,
    ].join("\n\n");

    navigator.clipboard.writeText(details).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, copied } = this.state;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-lg rounded-lg border border-border bg-card p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">
                Si è verificato un errore
              </h1>
              <p className="text-sm text-muted-foreground">
                L'applicazione ha riscontrato un problema imprevisto.
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 rounded-md border border-red-500/20 bg-red-500/5 p-4">
              <p className="text-sm font-mono text-red-700 dark:text-red-400 break-all">
                {error.message}
              </p>
            </div>
          )}

          <div className="text-xs text-muted-foreground mb-6 space-y-1">
            <p>Timestamp: {new Date().toISOString()}</p>
            <p>URL: {window.location.href}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Ricarica pagina
            </button>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Riprova
            </button>
            <button
              onClick={this.handleCopy}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Copy className="h-4 w-4" />
              {copied ? "Copiato!" : "Copia dettagli"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
